import React, { useCallback, useState } from "react";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useQuery } from "react-query";
import AdminActionButton from "../../../components/AdminActionButton";

type SubscriberRow = {
  id: string;
  email: string;
  subscribedAt: string;
};

const fetchNotifySubscribers = () =>
  fetch(`${process.env.REACT_APP_BASE_URL}/admin/notify-subscribers`, {
    method: "GET",
    headers: {
      "x-api-key": process.env.REACT_APP_API_KEY || "",
      "Content-Type": "application/json",
    },
  }).then(async (response) => {
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(typeof err.error === "string" ? err.error : "Request failed");
    }
    return response.json() as Promise<{ subscribers: { email: string; createdAt: string | null }[] }>;
  });

const NotifySubscribers = () => {
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ["notifySubscribers"],
    fetchNotifySubscribers,
    { refetchOnWindowFocus: false }
  );
  const [exporting, setExporting] = useState(false);

  const rows: SubscriberRow[] =
    data?.subscribers?.map((s) => ({
      id: s.email,
      email: s.email,
      subscribedAt: s.createdAt ? new Date(s.createdAt).toLocaleString() : "—",
    })) ?? [];

  const handleExport = useCallback(async () => {
    const base = process.env.REACT_APP_BASE_URL;
    if (!base) return;
    setExporting(true);
    try {
      const res = await fetch(`${base}/admin/notify-subscribers/export`, {
        headers: {
          "x-api-key": process.env.REACT_APP_API_KEY || "",
        },
      });
      if (!res.ok) {
        throw new Error("Export failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "inclusiviteee-notify-subscribers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert("Export failed. Check your connection and try again.");
    } finally {
      setExporting(false);
    }
  }, []);

  const columns: GridColDef[] = [
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 240,
    },
    {
      field: "subscribedAt",
      headerName: "Subscribed",
      width: 220,
    },
  ];

  return (
    <>
      {isLoading && <LinearProgress sx={{ borderRadius: 1, mb: 2 }} />}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            Email list
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Addresses collected from the site&apos;s notify form.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignSelf: { xs: "stretch", sm: "center" } }}>
          <AdminActionButton variant="outlined" loading={isFetching && !isLoading} onClick={() => refetch()}>
            Refresh
          </AdminActionButton>
          <AdminActionButton variant="contained" loading={exporting} onClick={handleExport} disabled={!rows.length}>
            Export CSV
          </AdminActionButton>
        </Stack>
      </Stack>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : "Could not load subscribers."}
        </Typography>
      )}

      {!error && !isLoading && data?.subscribers?.length === 0 && (
        <Typography color="text.secondary">No subscribers yet.</Typography>
      )}

      {rows.length > 0 && (
        <Box height={480} sx={{ width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            hideFooter={rows.length <= 10}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              "& .MuiDataGrid-columnHeaders": { bgcolor: "action.hover" },
            }}
          />
        </Box>
      )}

    </>
  );
};

export default NotifySubscribers;
